import React, { useEffect, useRef } from "react";
import { LuX } from "react-icons/lu";

// A native <dialog> rather than a hand-rolled overlay: the browser gives the focus trap, Esc, the
// inert background and returning focus to the CTA, none of which are worth shipping a library for.
export default function Dialog({
  labelledBy,
  width,
  onClose,
  children,
}: {
  labelledBy: string;
  // A full `tw-w-[…]` class, so Tailwind can see it.
  width: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    dialog.showModal();

    // showModal() leaves the page behind it scrollable, which drags the dialog off screen on iOS.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={labelledBy}
      onClose={onClose}
      // Anything outside the panel is the backdrop; the panel stops its own clicks below.
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
      className={`tw-m-auto ${width} tw-max-w-none tw-overflow-hidden tw-rounded-[28px] tw-border-0 tw-bg-white tw-p-0 tw-text-[#10233f] tw-shadow-[0_40px_90px_rgba(9,32,58,.35)]`}
    >
      {/* Outside the scroll container, so it stays reachable however far down the form the visitor is. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#c9deed] tw-bg-white tw-text-[#304d68] tw-transition-colors hover:tw-bg-[#eaf4fc] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3]"
      >
        <LuX aria-hidden className="tw-h-5 tw-w-5" />
      </button>

      <div className="tw-max-h-[min(46rem,90vh)] tw-overflow-y-auto">{children}</div>
    </dialog>
  );
}
